// frontend/src/services/api/utils/fetchWrapper.js
import { getAuth } from 'firebase/auth';

// Cache del token para evitar renovaciones innecesarias
let tokenCache = null;
let tokenExpiry = null;

// Helper para esperar a que Firebase Auth inicialice
const waitForAuthInit = async (auth, timeoutMs = 5000) => {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });

    // Timeout de seguridad
    setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
  });
};

export const getAuthHeaders = async () => {
  try {
    // Para panel admin, SIEMPRE usar Firebase Auth primero
    const isAdminPanel = window.location.pathname.includes('/admin-ops');

   if (isAdminPanel) {
  const auth = getAuth();

  // Esperar a que Firebase Auth termine de inicializar
  await waitForAuthInit(auth);

  const user = auth.currentUser;
  if (user) {
    let token;
    if (tokenCache && tokenExpiry && Date.now() < tokenExpiry) {
      token = tokenCache;
    } else {
      token = await user.getIdToken(true);
      tokenCache = token;
      tokenExpiry = Date.now() + (55 * 60 * 1000);
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  } else {
    // Si no hay usuario de Firebase en panel admin, es un error
    console.warn('⚠️ [Admin Panel] No hay usuario de Firebase autenticado');

    // IMPORTANTE: NO usar tokens de usuario final en panel admin
    // Limpiar cualquier token residual
    localStorage.removeItem('nfcToken');
    localStorage.removeItem('userToken');

    return {
      'Content-Type': 'application/json'
    };
  }
}

    // Para otras rutas, mantener la lógica actual
    // PRIORIDAD: URL > NFC > Firebase
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      return {
        'Authorization': `Bearer ${tokenFromUrl}`,
        'Content-Type': 'application/json'
      };
    }

    const nfcToken = localStorage.getItem('nfcToken');
    if (nfcToken) {
      return {
        'Authorization': `Bearer ${nfcToken}`,
        'Content-Type': 'application/json'
      };
    }

    // Si no hay token NFC ni URL, intentar con Firebase
    // IMPORTANTE: Esperar a que Firebase Auth inicialice
    const auth = getAuth();
    const user = await waitForAuthInit(auth);

    if (user) {
      let token;
      if (tokenCache && tokenExpiry && Date.now() < tokenExpiry) {
        token = tokenCache;
      } else {
        token = await user.getIdToken(true);
        tokenCache = token;
        tokenExpiry = Date.now() + (55 * 60 * 1000);
      }

      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    }
  } catch (error) {
    console.error('❌ Error obteniendo token:', error);
  }

  return {
    'Content-Type': 'application/json'
  };
};

// Función para limpiar el caché cuando se cierra sesión
export const clearTokenCache = () => {
  tokenCache = null;
  tokenExpiry = null;
};

// Notificación suave y no intrusiva
const mostrarNotificacionSesionExpirada = () => {
  // Remover notificación existente si hay
  const existing = document.getElementById('session-expired-notice');
  if (existing) existing.remove();
  
  const notice = document.createElement('div');
  notice.id = 'session-expired-notice';
  notice.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 8px;
      padding: 16px 24px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 400px;
      text-align: center;
      animation: slideDown 0.3s ease;
    ">
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="
          width: 40px;
          height: 40px;
          background: #ffeaa7;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        ">
          <span style="font-size: 20px;">⏰</span>
        </div>
        <div style="text-align: left;">
          <div style="font-weight: 600; color: #2d3436; margin-bottom: 4px;">
            Tu sesión expiró
          </div>
          <div style="color: #636e72; font-size: 14px;">
            Te llevaremos a solicitar un nuevo acceso...
          </div>
        </div>
      </div>
    </div>
  `;
  
  // Agregar estilos de animación
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translate(-50%, -20px);
      }
      to {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  `;
  document.head.appendChild(style);
  
  document.body.appendChild(notice);
  
  // Animación de salida
  setTimeout(() => {
    notice.style.opacity = '0';
    notice.style.transition = 'opacity 0.3s ease';
  }, 1700);
};

// Wrapper para manejar errores comunes
export const fetchWrapper = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    
    // Manejar 401/403 - Token expirado o sin permisos
    if (response.status === 401 || response.status === 403) {
      const isAdminPanel = window.location.pathname.includes('/admin-ops');
      const isUserPanel = window.location.pathname.includes('/userpanel') || 
                         window.location.pathname.includes('/user');
      
      if (isAdminPanel && response.status === 403) {
        console.error('❌ Acceso denegado en panel admin');
        
        // Para admin, intentar renovar el token una vez
        const auth = getAuth();
        const user = auth.currentUser;
        
        if (user) {
          try {
            // Forzar renovación del token
            const newToken = await user.getIdToken(true);
            tokenCache = newToken;
            tokenExpiry = Date.now() + (55 * 60 * 1000);
            
            // Reintentar la petición con el nuevo token
            const newOptions = {
              ...options,
              headers: {
                ...options.headers,
                'Authorization': `Bearer ${newToken}`
              }
            };
            
            const retryResponse = await fetch(url, newOptions);
            if (retryResponse.ok) {
              return retryResponse;
            }
          } catch (error) {
            console.error('❌ Error renovando token de admin:', error);
          }
        }
        
        // Si llega aquí, no hay usuario o falló la renovación
        console.error('❌ No se pudo autenticar como admin - redirigiendo...');
        alert('Tu sesión de administrador expiró. Por favor, inicia sesión nuevamente.');
        window.location.href = '/admin-ops';
        
        return {
          ok: false,
          status: 403,
          json: async () => ({ error: 'Sesión de admin expirada' }),
          text: async () => 'Sesión de admin expirada'
        };
      }
      
      if (isUserPanel && response.status === 401) {
        // Solo redirigir por 401 (token expirado/inválido), NO por 403 (permisos)
        // Intentar renovar token antes de redirigir
        const auth = getAuth();
        const user = await waitForAuthInit(auth);

        if (user) {
          try {
            const newToken = await user.getIdToken(true);
            tokenCache = newToken;
            tokenExpiry = Date.now() + (55 * 60 * 1000);

            const newOptions = {
              ...options,
              headers: {
                ...options.headers,
                'Authorization': `Bearer ${newToken}`
              }
            };

            const retryResponse = await fetch(url, newOptions);
            if (retryResponse.ok || retryResponse.status !== 401) {
              return retryResponse;
            }
          } catch (retryError) {
            console.warn('Error renovando token de usuario:', retryError);
          }
        }

        // Si no hay usuario o la renovación falló, sí redirigir
        // Prevenir loops: solo redirigir si no estamos ya en proceso de redirección
        if (!sessionStorage.getItem('redirectingToLogin')) {
          sessionStorage.setItem('redirectingToLogin', 'true');

          localStorage.removeItem('nfcToken');
          localStorage.removeItem('tokenType');
          sessionStorage.setItem('redirectAfterLogin', window.location.pathname + window.location.search);

          mostrarNotificacionSesionExpirada();

          setTimeout(() => {
            sessionStorage.removeItem('redirectingToLogin');
            window.location.href = '/login';
          }, 2000);
        }

        return {
          ok: false,
          status: response.status,
          json: async () => ({ error: 'Sesión expirada' }),
          text: async () => 'Sesión expirada'
        };
      }
    }
    
    return response;
  } catch (error) {
    console.error('❌ Error en fetchWrapper:', error);
    throw error;
  }
};